import React, { useState, useContext } from "react";
import { AuthContext } from "./context/AuthContext";
import { useTheme } from "./context/ThemeContext";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [capsLockWarning, setCapsLockWarning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Inicia loading
    try {
      await login(email, password);
      setSuccessMessage("Logado com sucesso!");
      setErrorMessage("");
      setTimeout(() => {
        navigate("/rastreio");
      }, 2000);
    } catch (error) {
      let message = "";
      if (error.message.includes("auth/user-not-found")) {
        message = "Erro, usuário não encontrado";
      } else if (error.message.includes("auth/wrong-password")) {
        message = "Erro, senha incorreta";
      } else if (error.message.includes("auth/invalid-credential")) {
        message = "Email ou senha inválidos";
      } else {
        message = "Erro no login: " + error.message;
      }
      setErrorMessage(message);
      setSuccessMessage("");
    } finally {
      setIsLoading(false); // Encerra loading
    }
  };

  return (
    <div
      className="h-screen w-full bg-cover bg-center bg-no-repeat flex justify-end items-center pr-40 relative"
      style={{
        backgroundImage: "url('/Login/Background.jpeg')",
      }}
    >
      
      <div className="absolute inset-0 bg-gradient-to-l from-salBlue to-transparent"></div>

      {/* Theme toggle button */}
      <div className="absolute top-5 left-5 z-20">
        <button
          onClick={toggleTheme}
          className="h-12 w-12 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-300 bg-white/20 backdrop-blur-md hover:bg-white/30"
        >
          {isDarkMode ? (
            <svg
              className="w-8 h-8 text-yellow-400 transition-transform duration-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              className="w-8 h-8 text-gray-800 transition-transform duration-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          )}
        </button>
      </div>

      <div className="absolute top-5 right-5">
        <img src="/Login/logo.png" alt="Logo SalExpress" className="h-12" />
      </div>

      <div className="h-[600px] w-[400px] border-2 border-white dark:border-dark-border rounded-xl bg-white-less/50 dark:bg-dark-surface/80 shadow-lg backdrop-blur-md flex items-center justify-center mr-20">
        <form
          className="flex flex-col items-center justify-center gap-10 w-11/12"
          onSubmit={handleLogin}
        >
          <h2 className="text-4xl font-extrabold text-center text-gray-800 dark:text-dark-text">
            Login
          </h2>
          <div className="flex flex-col gap-4 w-[80%]">
            <div className="flex flex-col">
              <label htmlFor="email" className="font-bold text-gray-800 dark:text-dark-text text-lg">
                E-mail:
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Digite seu e-mail"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                required
                className="w-full border-2 rounded-lg border-white dark:border-dark-border bg-gray-200/30 dark:bg-dark-card/50 shadow-md p-2 placeholder-gray-700 dark:placeholder-dark-textSecondary text-text dark:text-dark-text focus:outline-none focus:border-salBlue focus:bg-white dark:focus:bg-dark-surface focus:backdrop-blur"
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="password" className="font-bold text-gray-800 dark:text-dark-text text-lg">
                Senha:
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errorMessage) setErrorMessage("");
                  }}
                  autoFocus
                  onFocus={(e) => {
                    if (e.getModifierState && e.getModifierState("CapsLock")) {
                      setCapsLockWarning(true);
                    } else {
                      setCapsLockWarning(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    setCapsLockWarning(e.getModifierState && e.getModifierState("CapsLock"));
                  }}
                  onKeyUp={(e) => {
                    setCapsLockWarning(e.getModifierState && e.getModifierState("CapsLock"));
                  }}
                  required
                  className="w-full border-2 rounded-lg border-white dark:border-dark-border bg-gray-200/30 dark:bg-dark-card/50 shadow-md p-2 pr-10 placeholder-gray-700 dark:placeholder-dark-textSecondary text-text dark:text-dark-text focus:outline-none focus:border-salBlue focus:bg-white dark:focus:bg-dark-surface focus:backdrop-blur"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pl-2 pr-2 text-current"
                >
                  {showPassword ? (
                    // Ícone de olho fechado (ocultar senha)
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-[15px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.27-2.943-9.544-7a9.956 9.956 0 012.357-4.143M6.2 6.2A9.956 9.956 0 0112 5c4.478 0 8.27 2.943 9.544 7a9.956 9.956 0 01-1.19 2.398M15 12a3 3 0 00-3-3m0 0L9.878 9.878M12 9a3 3 0 013 3" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                    </svg>
                  ) : (
                    // Ícone de olho (ver senha)
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-[15px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="bg-gray-800 text-white-whiter font-bold text-xl py-2 px-8 rounded-lg shadow-lg hover:bg-salBlue transition-all hover:text-text disabled:opacity-50"
          >
            {isLoading ? "Carregando..." : "Entrar"}
          </button>

          {/* Container fixo para alertas: sempre reserva o mesmo espaço abaixo do botão */}
          <div className="alert-container w-full flex flex-col items-center justify-center" style={{ minHeight: "16px" }}>
            {successMessage && <p style={{ color: "green" }}>{successMessage}</p>}
            {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
            {capsLockWarning && <p className="text-sm text-gray-700">⚠ Caps Lock está ativado!</p>}
          </div>

          <p className="text-center text-gray-800 dark:text-white-whiter mt-4">
            Não tem uma conta?{" "}
            <a href="/registro" className="text-salBlue font-bold hover:underline">
              Registre-se aqui!
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}
